import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowRight, Heart, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-slate-50 to-slate-100" data-testid="footer">
      {/* CTA Banner - Blue Theme */}
      <div className="bg-gradient-to-r from-primary via-primary-700 to-primary">
        <div className="container-custom py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2">
                Ready to Experience Quality Healthcare?
              </h3>
              <p className="text-white/80 text-sm md:text-base font-body">
                Book an appointment today and let our expert team take care of you.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-appointment">
                <Button className="bg-white hover:bg-slate-100 text-primary rounded-lg px-8 py-3 font-semibold shadow-lg hover:shadow-xl transition-all group" data-testid="footer-cta-book">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="tel:+255659984488">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-lg px-8 py-3 font-semibold backdrop-blur-sm">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* About Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/5oi0txpb_Logooooo%20BH%20.png" 
                alt="Baobab Hospital Logo" 
                className="w-16 h-16 object-contain"
              />
              <div>
                <h3 className="font-heading font-bold text-primary text-lg tracking-tight">BAOBAB HOSPITAL</h3>
                <p className="text-xs text-slate-500 font-medium">Your Life is in Safe Hands</p>
              </div>
            </div>
            <p className="text-slate-600 mb-6 leading-relaxed text-sm font-body text-justify">
              Baobab Hospital is committed to quality, compassionate, and accessible healthcare for the community. We serve the Mapinga, Bagamoyo, and neighboring Pwani areas with dedication and excellence.
            </p>
            {/* Social Media */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Follow Us</p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm hover:shadow-md" data-testid="footer-facebook" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm hover:shadow-md" data-testid="footer-twitter" aria-label="Twitter">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm hover:shadow-md" data-testid="footer-instagram" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm hover:shadow-md" data-testid="footer-linkedin" aria-label="LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm hover:shadow-md" data-testid="footer-youtube" aria-label="YouTube">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-primary text-lg mb-6 pb-2 border-b-2 border-accent/30 inline-block">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about/background" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-about">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-departments">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Our Departments
                </Link>
              </li>
              <li>
                <Link to="/doctors" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-doctors">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Our Doctors
                </Link>
              </li>
              <li>
                <Link to="/find-doctor" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-find-doctor">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Find a Doctor
                </Link>
              </li>
              <li>
                <Link to="/executive-health" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-executive">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Executive Health
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-news">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  News & Updates
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-contact">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/admin" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body" data-testid="footer-admin">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Admin Panel
                </Link>
              </li>
            </ul>
          </div>

          {/* Services / Departments */}
          <div>
            <h4 className="font-heading font-semibold text-primary text-lg mb-6 pb-2 border-b-2 border-accent/30 inline-block">Our Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Outpatient Services
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Inpatient Services
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Maternal & Child Health
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Emergency Services
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Diagnostic Services
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Laboratory & Pharmacy
                </Link>
              </li>
              <li>
                <Link to="/departments" className="text-slate-600 hover:text-primary hover:translate-x-1 transition-all duration-200 flex items-center gap-2 text-sm group font-body">
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  Surgical Services
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-primary text-lg mb-6 pb-2 border-b-2 border-accent/30 inline-block">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 group">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0">
                  <MapPin className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-primary text-sm">Address</p>
                  <p className="text-slate-600 text-sm font-body">
                    Mapinga – Bagamoyo, Pwani<br />
                    P.O. Box 35692
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 group">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-primary text-sm">Phone Numbers</p>
                  <div className="text-slate-600 text-sm space-y-0.5 font-body">
                    <p>+255 659 984 488</p>
                    <p>+255 760 215 642</p>
                    <p>+255 780 635 888</p>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3 group">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-primary text-sm">Email</p>
                  <a href="mailto:admin@bhsp.info" className="text-slate-600 text-sm font-body hover:text-primary transition-colors block">admin@bhsp.info</a>
                  <a href="mailto:manager@bhsp.info" className="text-slate-600 text-sm font-body hover:text-primary transition-colors block">manager@bhsp.info</a>
                </div>
              </li>
              <li className="flex items-start gap-3 group">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow flex-shrink-0">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium text-primary text-sm">Operating Hours</p>
                  <p className="text-accent font-semibold text-sm">Open 24/7</p>
                  <p className="text-slate-600 text-xs font-body">7 Days a Week</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200 bg-white/50">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600 text-sm font-body">
              <span>© {currentYear} Baobab Hospital. All Rights Reserved.</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm font-body">
              <Link to="/privacy-policy" className="text-slate-600 hover:text-primary transition-colors font-medium" data-testid="footer-bottom-privacy">
                Privacy Policy
              </Link>
              <span className="text-slate-300">|</span>
              <Link to="/contact" className="text-slate-600 hover:text-primary transition-colors font-medium">
                Contact
              </Link>
              <span className="text-slate-300">|</span>
              <Link to="/book-appointment" className="text-primary hover:text-primary-700 transition-colors font-semibold" data-testid="footer-bottom-appointment">
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
